'use client'

import { useEffect } from 'react'
import { attachCircuitBreakerConsumer } from '@/lib/features/network-status/circuitBreakerConsumer'

/**
 * T-NOTIFIER-1 (2026-04-19).
 *
 * Root-layout client provider that attaches every client-side bus
 * consumer at mount. Before today `attachCircuitBreakerConsumer()`
 * was defined in `lib/features/network-status/circuitBreakerConsumer.ts`
 * but never called — the file's own comment said "Client boot:
 * import `attachCircuitBreakerConsumer()` from a root client
 * provider (e.g. a `<TelemetryConsumersProvider>` mounted in the
 * root layout)". This is that provider.
 *
 * Scope today:
 *   • CircuitBreaker consumer — paired with `<NetworkStatusBanner>`
 *     which reads `useCircuitBreakerStatus()`.
 *
 * Scope deferred:
 *   • DuplicateDetectedConsumer is NOT subscription-based — it
 *     reads `duplicates` off the `/api/entries` POST response and
 *     calls `usePresentationStore.present(...)`. Each call site
 *     that POSTs needs to call `useDuplicateDetectedConsumer()
 *     .handleEntrySaveResponse(response)`. Tracked as T-NOTIFIER-2.
 *
 * Mounts above `{children}` in `src/app/layout.tsx` alongside the
 * existing `<ToastProvider>` / `<VoiceManager>` etc.
 */
export function TelemetryConsumersProvider({
    children,
}: {
    children: React.ReactNode
}) {
    useEffect(() => {
        const dispose = attachCircuitBreakerConsumer()
        return () => {
            dispose()
        }
    }, [])

    return <>{children}</>
}
